import { useTheme } from '@emotion/react'
import AddIcon from '@mui/icons-material/Add'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckIcon from '@mui/icons-material/Check'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import DifferenceIcon from '@mui/icons-material/Difference'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import EditIcon from '@mui/icons-material/Edit'
import EmailIcon from '@mui/icons-material/Email'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import GridOnIcon from '@mui/icons-material/GridOn'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import InsightsIcon from '@mui/icons-material/Insights'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import ListIcon from '@mui/icons-material/List'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PrintIcon from '@mui/icons-material/Print'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RemoveDoneIcon from '@mui/icons-material/RemoveDone'
import ReplayIcon from '@mui/icons-material/Replay'
import TabIcon from '@mui/icons-material/Tab'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import TimelineIcon from '@mui/icons-material/Timeline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    Menu,
    MenuItem,
    Select,
    Stack,
    SxProps,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoading } from '../../context/loading_context'
import { EmptyState } from '../EmptyState'
import { FilterBox } from '../FilterBox'
import { GenericAccordion } from '../GenericAccordion'
import GenericModal from '../GenericModal'
import { GenericTable2 } from '../GenericTable2'
import { StringField } from '../StringField'
import { StyledPaper } from '../StyledPaper'

function getDefaultIcons(name: string) {
    if (name === 'Nuevo') return <AddIcon />
    if (name.startsWith('Imprimir')) return <PrintIcon />
    if (name.includes('Cerrar')) return <DoneAllIcon />
    if (name.startsWith('Cancelar')) return <CancelIcon />
    if (name.startsWith('Juntar')) return <FormatListNumberedIcon />
    if (name.includes('email')) return <EmailIcon />
    if (name === 'Acciones') return <CheckBoxIcon />
    if (name === 'Relatórios') return <InsertDriveFileIcon />
    if (name.includes('PDF')) return <PictureAsPdfIcon />
    if (name.includes('Excel')) return <GridOnIcon />
    if (name.includes('excel')) return <GridOnIcon />
    if (name.includes('CSV')) return <GridOnIcon />
    if (name.includes('RG90')) return <GridOnIcon />
    if (name.includes('TXT')) return <TextSnippetIcon />
    if (name.includes('Generar Relatório Legal')) return <TextSnippetIcon />
    if (name.includes('Datos')) return <InsightsIcon />
    if (name.includes('Editar')) return <EditIcon />
    if (name.includes('Activar')) return <DoneAllIcon />
    if (name.includes('Desactivar')) return <RemoveDoneIcon />
    if (name.includes('Duplicar')) return <DifferenceIcon />
    if (name.includes('Deletar')) return <DeleteIcon />
    if (name.includes('Generar movimiento')) return <AddCircleIcon />
    if (name.includes('Movimientos')) return <TimelineIcon />
    if (name.includes('Visualizar')) return <VisibilityIcon />
    if (name.includes('Operaciónes')) return <TabIcon />
    if (name.includes('Ventas')) return <ListIcon />
    if (name.includes('nota de crédito')) return <KeyboardReturnIcon />
    if (name.includes('Entrega')) return <LocalShippingIcon />
    if (name.includes('Seleccionar')) return <CheckIcon />
    if (name.includes('WhatsApp')) return <WhatsAppIcon />
}

interface NoRowsDataGridProps {
    loading?: boolean
    sx?: SxProps
}

function NoRowsDataGrid({ loading, sx }: NoRowsDataGridProps) {
    const { t } = useTranslation()

    if (loading)
        return (
            <Stack
                width={'100%'}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ py: '36px', mt: '12px' }}
            >
                <CircularProgress size="40px" />
            </Stack>
        )

    return (
        <Box
            sx={{
                border: '1px dashed',
                borderColor: '#C2C8D6',
                borderRadius: '12px',
                mt: '12px',
                backgroundColor: 'var(--surface-soft)',
                ...sx,
            }}
        >
            <EmptyState
                icon={<ReceiptLongIcon sx={{ fontSize: '30px' }} />}
                title={t('Ningún resultado encontrado')}
                description={t(
                    'Ajuste a busca ou os filtros e tente novamente'
                )}
            />
        </Box>
    )
}

interface Filter {
    name: string
    labelField: string
    valueField: string
    values: any[]
    selectedValues: any[]
    autoComplete?: {
        cb: (v: string) => void
    }
    hideSelection?: boolean
}

interface CheckboxFilter {
    name: string
    value: boolean
}

interface Column {
    name: string
    property: string[]
    customRender?(row: any, value: any): JSX.Element
    parser?(value: any): any
    hideSm?: boolean
    hideXs?: boolean
    flex?: number
    defaultHidden?: boolean
}

interface Pagination {
    page: number
    rows: number
    rowsCount?: number
    handleChangePagination(page: number, rows: number): void
}

interface Action {
    name: string
    dropdownActions?: Action[]
    color:
        | 'inherit'
        | 'primary'
        | 'secondary'
        | 'success'
        | 'error'
        | 'info'
        | 'warning'
    hide?: boolean
    icon?: JSX.Element
}

export interface GenericTableProps {
    title: string
    filters: Filter[]
    checkboxFilters?: CheckboxFilter[]
    columns: Column[]
    values: any[]
    identifier: string
    selectedValues: (number | string)[]
    subValueField?: string[]
    subValueIndentifier?: string
    isRecursive?: boolean
    pagination?: Pagination
    actions?: Action[]
    search: string
    withoutPaper?: boolean
    withoutSelection?: boolean
    hideFilters?: boolean
    hideSearch?: boolean
    hidePagination?: boolean
    handleChangeSearch(value: string): void
    handleExecuteAction?(action: string): void
    handleChangeSelectedFilter(selectedFilter: {
        name: string
        value: any
        action: 'add' | 'remove'
    }): void
    handleChangeSelectedValues(values: (number | string)[]): void
    handleClickRow?(identifier: any): void
    handleClickDetails?(identifier: any): void
    handleReload?(): void
    sx?: SxProps
}

export function GenericTable({
    title,
    filters,
    checkboxFilters,
    columns,
    values,
    identifier,
    selectedValues,
    subValueField,
    subValueIndentifier,
    isRecursive,
    pagination,
    actions,
    withoutPaper,
    withoutSelection,
    hideFilters,
    hideSearch,
    hidePagination,
    search,
    handleChangeSearch,
    handleExecuteAction,
    handleChangeSelectedFilter,
    handleChangeSelectedValues,
    handleClickRow,
    handleClickDetails,
    handleReload,
    sx,
}: GenericTableProps) {
    const { t } = useTranslation()
    const theme: any = useTheme()
    const isSm = useMediaQuery(theme.breakpoints.down('md'))
    const isXs = useMediaQuery(theme.breakpoints.only('xs'))
    const [dropdownActionsAnchorEl, setDropdownActionsAnchorEl] =
        useState<HTMLElement | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout>()
    const [intervalReload, setIntervalReload] = useState(0)
    const [currentIntervalReload, setCurrentIntervalReload] = useState(0)
    const [intervalReloadAnchorEl, setIntervalReloadAnchorEl] =
        useState<HTMLElement | null>(null)
    const [openSelectedItemsModal, setOpenSelectedItemsModal] = useState(false)
    const [selectedItemsData, setSelectedItemsData] = useState<any[] | null>(
        null
    )
    const selectedAction = useRef<Action | null>(null)
    const [openFiltersModal, setOpenFiltersModal] = useState(false)

    const [showSubValues, setShowSubValues] = useState<
        { id: any; show: boolean }[]
    >(values.map((value) => ({ id: value[identifier], show: false })))

    const { isLoading, isLoadingFullScreen } = useLoading()

    const valuesSum = values
        .map((value) => value[identifier])
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0)

    useEffect(() => {
        setShowSubValues(
            values.map((value) => ({ id: value[identifier], show: false }))
        )
    }, [valuesSum])

    useEffect(() => {}, [intervalReload])

    const getSubValues = (value: any): any[] | null => {
        if (!subValueField) return null
        for (const property of subValueField) {
            if (value && value.hasOwnProperty(property)) value = value[property]
            else return null
        }
        return value
    }

    function getRows(): any[] {
        if (!subValueField) return values

        const newRows: any[] = []

        values.forEach((value, index) => {
            newRows.push(value)
            const subValues = getSubValues(value)
            if (
                subValues &&
                Array.isArray(subValues) &&
                showSubValues[index]?.show
            )
                for (const subValue of subValues) {
                    newRows.push({
                        ...subValue,
                        isSubValue: true,
                    })
                }
        })

        return newRows
    }

    const handleChangeShowSubValues = (id: any, value: boolean) => {
        const newShowSubValues = [...showSubValues]
        const index = newShowSubValues.findIndex((v) => v.id === id)
        newShowSubValues[index].show = value
        setShowSubValues(newShowSubValues)
        if (handleClickRow && value) handleClickRow(id)
    }

    const TablePaper = withoutPaper ? Box : StyledPaper

    const newValues = getRows()

    const filtersLength = filters.filter((f) => !f.hideSelection).length
    const selectedWidth =
        filtersLength === 1
            ? 100
            : 100 / filters.filter((f) => !f.hideSelection).length - 1

    const handleCloseDropdownActions = () => {
        setDropdownActionsAnchorEl(null)
        selectedAction.current = null
    }

    const handleCloseIntervalMenu = () => {
        setIntervalReloadAnchorEl(null)
    }

    const handleChangeInterval = (v: number) => {
        setIntervalReload(v)
        clearTimeout(timeoutRef.current)
        setCurrentIntervalReload(0)
    }

    useEffect(() => {
        if (!intervalReload || !handleReload) {
            clearTimeout(timeoutRef.current)
            return
        }

        timeoutRef.current = setTimeout(() => {
            const value = currentIntervalReload + 1
            if (value === intervalReload) {
                handleReload()
                setCurrentIntervalReload(0)
            } else {
                setCurrentIntervalReload(value)
            }
        }, 1000)

        return () => {
            clearTimeout(timeoutRef.current)
        }
    }, [intervalReload, currentIntervalReload])

    const FiltersComponent = isSm ? Dialog : Fragment

    const getSelectedItemLabel = (item: any) => {
        const col = columns[0]
        if (!col) return String(item[identifier] ?? '')

        let value: any = item
        for (const property of col.property) {
            if (value && value.hasOwnProperty(property)) value = value[property]
            else return String(item[identifier] ?? '')
        }
        value = col.parser ? col.parser(value) : value

        return value ?? String(item[identifier] ?? '')
    }

    const handleOpenSelectedItems = () => {
        setSelectedItemsData(
            selectedValues.map(
                (id) =>
                    values.find((v) => v[identifier] === id) ?? {
                        [identifier]: id,
                    }
            )
        )
        setOpenSelectedItemsModal(true)
    }

    const handleRemoveSelectedItem = (id: any) => {
        handleChangeSelectedValues(selectedValues.filter((v) => v !== id))
        if (selectedItemsData)
            setSelectedItemsData(
                selectedItemsData.filter((item) => item[identifier] !== id)
            )
    }

    const handleClearSelected = () => {
        handleChangeSelectedValues([])
        setSelectedItemsData(null)
        setOpenSelectedItemsModal(false)
    }

    return (
        <>
            <Menu
                anchorEl={intervalReloadAnchorEl}
                open={Boolean(intervalReloadAnchorEl)}
                onClose={handleCloseIntervalMenu}
            >
                <MenuItem
                    onClick={() => {
                        handleReload && handleReload()
                        handleCloseIntervalMenu()
                    }}
                >
                    {t('Recarregar agora')}
                </MenuItem>
                {[5, 15, 30, 60].map((v) => (
                    <MenuItem
                        onClick={() => {
                            handleChangeInterval(v)
                            handleCloseIntervalMenu()
                        }}
                    >
                        {t('Recarregar a cada')} {v} {t('segundos')}
                    </MenuItem>
                ))}
                {intervalReload > 0 && (
                    <MenuItem
                        onClick={() => {
                            handleChangeInterval(0)
                            handleCloseIntervalMenu()
                        }}
                    >
                        {t('Parar de carregar')}
                    </MenuItem>
                )}
            </Menu>
            <Menu
                anchorEl={dropdownActionsAnchorEl}
                open={Boolean(dropdownActionsAnchorEl)}
                onClose={handleCloseDropdownActions}
            >
                {selectedAction.current?.dropdownActions
                    ?.filter((action) => !action.hide)
                    .map((action) => (
                        <MenuItem
                            sx={{
                                width: { xs: '100%' },
                            }}
                            onClick={() => {
                                if (handleExecuteAction)
                                    handleExecuteAction(action.name)
                                handleCloseDropdownActions()
                            }}
                        >
                            {action.icon
                                ? action.icon
                                : getDefaultIcons(action.name)}
                            <Box ml="10px">{t(action.name)}</Box>
                        </MenuItem>
                    ))}
            </Menu>
            <GenericModal
                open={openSelectedItemsModal}
                handleClose={() => setOpenSelectedItemsModal(false)}
                handleSubmit={() => setOpenSelectedItemsModal(false)}
                title="Elementos seleccionados"
                textButton="Cerrar"
            >
                <Stack spacing={1}>
                    {selectedItemsData?.map((item, index) => (
                        <Stack
                            key={index}
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            spacing={1}
                            sx={{
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                px: '12px',
                                py: '8px',
                                backgroundColor: 'var(--surface-soft)',
                            }}
                        >
                            <Typography
                                fontSize="14px"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                }}
                            >
                                {getSelectedItemLabel(item)}
                            </Typography>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                    handleRemoveSelectedItem(item[identifier])
                                }
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                    {!selectedItemsData?.length && (
                        <Typography
                            color="text.secondary"
                            textAlign="center"
                            py={'20px'}
                        >
                            {t('Ningún elemento seleccionado')}
                        </Typography>
                    )}
                    {!!selectedItemsData?.length && (
                        <Button
                            variant="text"
                            color="error"
                            onClick={handleClearSelected}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('Quitar todos')}
                        </Button>
                    )}
                </Stack>
            </GenericModal>
            <TablePaper sx={{ ...sx }}>
                {!hideSearch && (
                    <Stack
                        width={'100%'}
                        direction={'row'}
                        alignItems={'center'}
                        mb={!hideFilters && filters.length ? '15px' : '5px'}
                    >
                        <StringField
                            label="Buscar"
                            maxLength={100}
                            value={search}
                            setValue={(v) => {
                                handleChangeSearch(v)
                            }}
                            sx={{
                                width: '100%',
                            }}
                            size="small"
                            endAdornment={
                                handleReload ? (
                                    <InputAdornment position="end">
                                        <Tooltip
                                            title={t('Recarregar agora')}
                                            arrow
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={(e) =>
                                                    setIntervalReloadAnchorEl(
                                                        e.currentTarget
                                                    )
                                                }
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                <ReplayIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : undefined
                            }
                        />
                    </Stack>
                )}
                {intervalReload > 0 && handleReload && (
                    <LinearProgress
                        variant="determinate"
                        value={
                            ((intervalReload - currentIntervalReload) /
                                intervalReload) *
                            100
                        }
                        sx={{
                            height: '4px',
                            borderRadius: '99px',
                            mb: '12px',
                        }}
                    />
                )}
                {isSm && !hideFilters && (
                    <Typography
                        color="primary"
                        sx={{
                            textDecoration: 'underline',
                            fontWeight: 600,
                            my: '10px',
                            cursor: 'pointer',
                            fontSize: '14px',
                        }}
                        onClick={() => setOpenFiltersModal(true)}
                    >
                        {t('Exibir filtros')}
                    </Typography>
                )}
                <FiltersComponent
                    open={openFiltersModal}
                    onClose={() => setOpenFiltersModal(false)}
                >
                    {isSm && (
                        <DialogTitle>
                            <Box
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    textAlign: 'left',
                                    width: { xs: '300px' },
                                }}
                            >
                                {t('Filtros')}
                            </Box>
                        </DialogTitle>
                    )}
                    <Box sx={{ p: isSm ? '15px' : '0' }}>
                        {!hideFilters && (
                            <>
                                <Stack
                                    flexDirection={'row'}
                                    sx={{ width: '100%' }}
                                    justifyContent={'space-between'}
                                    flexWrap={'wrap'}
                                >
                                    {filters
                                        .filter((f) => !f.hideSelection)
                                        .map((filter) => {
                                            if (filter.autoComplete) {
                                                return (
                                                    <Autocomplete
                                                        options={filter.values}
                                                        getOptionLabel={(
                                                            value
                                                        ) =>
                                                            value[
                                                                filter
                                                                    .labelField
                                                            ]
                                                        }
                                                        filterOptions={(
                                                            options
                                                        ) => options}
                                                        sx={{
                                                            width: {
                                                                xs: '100%',
                                                                md: `${selectedWidth}%`,
                                                            },
                                                            mb: {
                                                                xs: '10px',
                                                                md: '0',
                                                            },
                                                        }}
                                                        value={null}
                                                        onChange={(
                                                            e,
                                                            value
                                                        ) => {
                                                            const foundSelectedValue =
                                                                filter.selectedValues.find(
                                                                    (v) =>
                                                                        v[
                                                                            filter
                                                                                .valueField
                                                                        ] ===
                                                                        value[
                                                                            filter
                                                                                .valueField
                                                                        ]
                                                                )
                                                            if (
                                                                value &&
                                                                !foundSelectedValue
                                                            )
                                                                handleChangeSelectedFilter(
                                                                    {
                                                                        name: filter.name,
                                                                        value: value,
                                                                        action: 'add',
                                                                    }
                                                                )
                                                        }}
                                                        size="small"
                                                        blurOnSelect={true}
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    filter.autoComplete?.cb(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }}
                                                                label={t(
                                                                    filter.name
                                                                )}
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                                onFocus={() =>
                                                                    filter.autoComplete?.cb(
                                                                        ''
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )
                                            }

                                            return (
                                                <FormControl
                                                    sx={{
                                                        width: {
                                                            xs: '100%',
                                                            md: `${selectedWidth}%`,
                                                        },
                                                        mb: {
                                                            xs: '10px',
                                                            md: '0',
                                                        },
                                                    }}
                                                    size="small"
                                                >
                                                    <InputLabel>
                                                        {t(filter.name)}
                                                    </InputLabel>
                                                    <Select
                                                        label={t(filter.name)}
                                                        fullWidth
                                                        onChange={(e) => {
                                                            const { value } =
                                                                e.target
                                                            const foundValue =
                                                                filter.values.find(
                                                                    (v) =>
                                                                        v[
                                                                            filter
                                                                                .valueField
                                                                        ] ===
                                                                        value
                                                                )
                                                            const foundSelectedValue =
                                                                filter.selectedValues.find(
                                                                    (v) =>
                                                                        v[
                                                                            filter
                                                                                .valueField
                                                                        ] ===
                                                                        value
                                                                )

                                                            if (
                                                                foundValue &&
                                                                !foundSelectedValue
                                                            )
                                                                handleChangeSelectedFilter(
                                                                    {
                                                                        name: filter.name,
                                                                        value: foundValue,
                                                                        action: 'add',
                                                                    }
                                                                )
                                                        }}
                                                        value={''}
                                                    >
                                                        {filter.values.map(
                                                            (value) => (
                                                                <MenuItem
                                                                    value={
                                                                        value[
                                                                            filter
                                                                                .valueField
                                                                        ]
                                                                    }
                                                                >
                                                                    {t(
                                                                        value[
                                                                            filter
                                                                                .labelField
                                                                        ]
                                                                    )}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            )
                                        })}
                                </Stack>
                                <Stack direction={'row'} flexWrap={'wrap'}>
                                    {filters.map((f) =>
                                        f.selectedValues.map((value, i) => (
                                            <FilterBox
                                                name={t(f.name)}
                                                value={t(value[f.labelField])}
                                                handleRemove={() => {
                                                    handleChangeSelectedFilter({
                                                        name: f.name,
                                                        value: value[
                                                            f.valueField
                                                        ],
                                                        action: 'remove',
                                                    })
                                                }}
                                            />
                                        ))
                                    )}
                                </Stack>
                                <Stack direction={'row'} flexWrap={'wrap'}>
                                    {checkboxFilters?.map((filter) => (
                                        <FormControlLabel
                                            label={t(filter.name)}
                                            sx={{ mt: '10px' }}
                                            control={
                                                <Checkbox
                                                    checked={filter.value}
                                                    onChange={(e) => {
                                                        handleChangeSelectedFilter(
                                                            {
                                                                name: filter.name,
                                                                value: e.target
                                                                    .checked,
                                                                action: 'add',
                                                            }
                                                        )
                                                    }}
                                                />
                                            }
                                        />
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Box>
                </FiltersComponent>
                {!newValues.length ? (
                    <NoRowsDataGrid
                        loading={isLoading && !isLoadingFullScreen}
                        sx={sx}
                    />
                ) : (
                    <>
                        {isSm ? (
                            <GenericAccordion
                                values={values}
                                columns={columns}
                                pagination={pagination}
                                handleClickDetails={handleClickDetails}
                                identifier={identifier}
                                selectedValues={selectedValues}
                                handleChangeSelectedValues={
                                    handleChangeSelectedValues
                                }
                                handleOpenSelectedItems={
                                    handleOpenSelectedItems
                                }
                                withoutSelection={withoutSelection}
                            />
                        ) : (
                            <GenericTable2
                                title={title}
                                values={values}
                                columns={columns}
                                pagination={pagination}
                                handleClickDetails={handleClickDetails}
                                identifier={identifier}
                                selectedValues={selectedValues}
                                handleChangeSelectedValues={
                                    handleChangeSelectedValues
                                }
                                handleOpenSelectedItems={
                                    handleOpenSelectedItems
                                }
                                withoutSelection={withoutSelection}
                            />
                        )}
                    </>
                )}
                <Stack
                    direction={'row'}
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                    alignItems={'center'}
                    flexWrap={'wrap'}
                >
                    {actions ? (
                        actions
                            .filter((action) => !action.hide)
                            .map((action) => (
                                <Button
                                    variant="contained"
                                    color={action.color}
                                    disableElevation
                                    sx={{
                                        ml: { xs: '0px', md: '10px' },
                                        mt: { xs: '10px' },
                                        textTransform: 'none',
                                        width: { xs: '100%', md: '200px' },
                                        py: '10px',
                                    }}
                                    onClick={(event) => {
                                        if (action.dropdownActions) {
                                            selectedAction.current = action
                                            setDropdownActionsAnchorEl(
                                                event.currentTarget
                                            )
                                        } else if (handleExecuteAction) {
                                            handleExecuteAction(action.name)
                                        }
                                    }}
                                    startIcon={
                                        action.icon
                                            ? action.icon
                                            : getDefaultIcons(action.name)
                                    }
                                    endIcon={
                                        action.dropdownActions ? (
                                            <KeyboardArrowDownIcon />
                                        ) : undefined
                                    }
                                >
                                    {t(action.name)}
                                </Button>
                            ))
                    ) : (
                        <></>
                    )}
                </Stack>
            </TablePaper>
        </>
    )
}
